import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Brain, Pill, AlertTriangle, CheckCircle, FileText, Heart,
  Activity, RefreshCw, Search, Plus, Lightbulb, Shield, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClinicalDecisionSupport = () => {
  const [activeTab, setActiveTab] = useState('interactions');
  const [loading, setLoading] = useState(false);
  const [medications, setMedications] = useState('');
  const [interactionResults, setInteractionResults] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  
  const [patientData, setPatientData] = useState({
    diagnoses: '',
    symptoms: '',
    vitals: { pain_score: 0, respiratory_rate: 18, oxygen_saturation: 96 },
    medications: '',
    pps_score: 70
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadProtocols();
  }, []);

  const loadProtocols = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/cds/symptom-protocols`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProtocols(data.protocols || []);
      }
    } catch (error) {
      console.error('Error loading protocols:', error);
    }
  };

  const checkInteractions = async () => {
    if (!medications.trim()) {
      toast.error('Please enter medications');
      return;
    }

    setLoading(true);
    try {
      const medList = medications.split(',').map(m => m.trim()).filter(m => m);
      const response = await fetch(`${API_URL}/api/phase31/cds/drug-interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ medications: medList })
      });

      if (response.ok) {
        const data = await response.json();
        setInteractionResults(data);
        if (data.interactions_found > 0) {
          toast.warning(`Found ${data.interactions_found} potential interaction(s)`);
        } else {
          toast.success('No significant interactions found');
        }
      }
    } catch (error) {
      toast.error('Error checking interactions');
    }
    setLoading(false);
  };

  const getRecommendations = async () => {
    setLoading(true);
    try {
      const payload = {
        diagnoses: patientData.diagnoses.split(',').map(d => d.trim()).filter(d => d),
        symptoms: patientData.symptoms.split(',').map(s => s.trim()).filter(s => s),
        vitals: patientData.vitals,
        medications: patientData.medications.split(',').map(m => m.trim()).filter(m => m),
        functional_status: { mobility: 'needs_assistance', pps_score: patientData.pps_score }
      };

      const response = await fetch(`${API_URL}/api/phase31/cds/recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setRecommendations(data.recommendations || []);
        toast.success(`Generated ${data.count} recommendations`);
      }
    } catch (error) {
      toast.error('Error generating recommendations');
    }
    setLoading(false);
  };

  const checkEligibility = async () => {
    setLoading(true);
    try {
      const payload = {
        diagnoses: patientData.diagnoses.split(',').map(d => d.trim()).filter(d => d),
        functional_status: { pps_score: patientData.pps_score },
        clinical_markers: {},
        hospitalizations_6mo: 2,
        weight_change_percent: -12
      };

      const response = await fetch(`${API_URL}/api/phase31/cds/hospice-eligibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setEligibilityResult(data);
      }
    } catch (error) {
      toast.error('Error checking eligibility');
    }
    setLoading(false);
  };

  const loadProtocol = async (protocolId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/cds/symptom-protocol/${protocolId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedProtocol(data);
      }
    } catch (error) {
      toast.error('Error loading protocol');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      severe: 'bg-red-100 text-red-800',
      moderate: 'bg-yellow-100 text-yellow-800',
      minor: 'bg-blue-100 text-blue-800'
    };
    return <Badge className={styles[severity]}>{severity.toUpperCase()}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return <Badge className={styles[priority]}>{priority.toUpperCase()}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="clinical-decision-support">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Brain className="h-8 w-8 text-teal-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clinical Decision Support</h1>
          <p className="text-gray-500">AI-powered clinical recommendations and guidelines</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="interactions">Drug Interactions</TabsTrigger>
          <TabsTrigger value="recommendations">Care Recommendations</TabsTrigger>
          <TabsTrigger value="eligibility">Hospice Eligibility</TabsTrigger>
          <TabsTrigger value="protocols">Symptom Protocols</TabsTrigger>
        </TabsList>

        {/* Drug Interactions Tab */}
        <TabsContent value="interactions" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pill className="h-5 w-5" />
                Drug Interaction Checker
              </CardTitle>
              <CardDescription>Check for potential drug-drug interactions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Medications (comma-separated)</Label>
                <Textarea
                  value={medications}
                  onChange={(e) => setMedications(e.target.value)}
                  placeholder="e.g., Warfarin, Aspirin, Morphine, Lorazepam"
                  rows={3}
                />
              </div>
              <Button onClick={checkInteractions} disabled={loading}>
                {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Check Interactions
              </Button>

              {interactionResults && (
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <Badge className={interactionResults.overall_risk === 'high' ? 'bg-red-500' : interactionResults.overall_risk === 'moderate' ? 'bg-yellow-500' : 'bg-green-500'}>
                      Overall Risk: {interactionResults.overall_risk.toUpperCase()}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {interactionResults.interactions_found} interaction(s) found
                    </span>
                  </div>

                  {interactionResults.interactions.map((interaction, idx) => (
                    <Card key={idx} className={interaction.severity === 'severe' ? 'border-red-300' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className={`h-5 w-5 ${interaction.severity === 'severe' ? 'text-red-500' : 'text-yellow-500'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{interaction.drug1} + {interaction.drug2}</span>
                              {getSeverityBadge(interaction.severity)}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{interaction.description}</p>
                            <p className="text-sm text-teal-600 mt-1">
                              <strong>Recommendation:</strong> {interaction.recommendation}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Care Recommendations Tab */}
        <TabsContent value="recommendations" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5" />
                AI Care Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Diagnoses (comma-separated)</Label>
                  <Input
                    value={patientData.diagnoses}
                    onChange={(e) => setPatientData({...patientData, diagnoses: e.target.value})}
                    placeholder="e.g., Cancer, CHF, COPD"
                  />
                </div>
                <div>
                  <Label>Current Symptoms</Label>
                  <Input
                    value={patientData.symptoms}
                    onChange={(e) => setPatientData({...patientData, symptoms: e.target.value})}
                    placeholder="e.g., poor_appetite, weight_loss"
                  />
                </div>
                <div>
                  <Label>Pain Score (0-10)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={patientData.vitals.pain_score}
                    onChange={(e) => setPatientData({
                      ...patientData, 
                      vitals: {...patientData.vitals, pain_score: parseInt(e.target.value) || 0}
                    })}
                  />
                </div>
                <div>
                  <Label>Medications</Label>
                  <Input
                    value={patientData.medications}
                    onChange={(e) => setPatientData({...patientData, medications: e.target.value})}
                    placeholder="e.g., Morphine, Gabapentin"
                  />
                </div>
              </div>
              <Button onClick={getRecommendations} disabled={loading}>
                {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                Generate Recommendations
              </Button>

              {recommendations.length > 0 && (
                <div className="space-y-3 mt-4">
                  {recommendations.map((rec, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{rec.title}</h4>
                              {getPriorityBadge(rec.priority)}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                            <div className="mt-2">
                              <p className="text-sm font-medium">Actions:</p>
                              <ul className="text-sm text-gray-600 list-disc list-inside">
                                {rec.actions.slice(0, 3).map((action, i) => (
                                  <li key={i}>{action}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          <Badge variant="outline" className="capitalize">{rec.category.replace('_', ' ')}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hospice Eligibility Tab */}
        <TabsContent value="eligibility" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Hospice Eligibility Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Primary Diagnoses</Label>
                  <Input
                    value={patientData.diagnoses}
                    onChange={(e) => setPatientData({...patientData, diagnoses: e.target.value})}
                    placeholder="e.g., Lung Cancer, Heart Failure"
                  />
                </div>
                <div>
                  <Label>PPS Score (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={patientData.pps_score}
                    onChange={(e) => setPatientData({...patientData, pps_score: parseInt(e.target.value) || 70})}
                  />
                </div>
              </div>
              <Button onClick={checkEligibility} disabled={loading}>
                Assess Eligibility
              </Button>

              {eligibilityResult && (
                <div className="mt-4 space-y-4">
                  <Card className={eligibilityResult.eligible ? 'border-green-300 bg-green-50' : 'border-yellow-300 bg-yellow-50'}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {eligibilityResult.eligible ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-6 w-6 text-yellow-600" />
                        )}
                        <div>
                          <p className="font-medium">{eligibilityResult.recommendation}</p>
                          <p className="text-sm text-gray-600">
                            Confidence: {eligibilityResult.confidence} | Score: {eligibilityResult.eligibility_score}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium mb-2 text-green-700">Criteria Met</h4>
                      {eligibilityResult.criteria_met.map((c, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm py-1">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>{c.criteria}: {c.value}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="font-medium mb-2 text-yellow-700">Criteria Not Met</h4>
                      {eligibilityResult.criteria_not_met.map((c, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm py-1">
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          <span>{c.criteria}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Symptom Protocols Tab */}
        <TabsContent value="protocols" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Protocols</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {protocols.map((protocol) => (
                    <Button
                      key={protocol.id}
                      variant={selectedProtocol?.symptom?.toLowerCase() === protocol.id ? 'default' : 'outline'}
                      className="w-full justify-start"
                      onClick={() => loadProtocol(protocol.id)}
                    >
                      {protocol.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">
                  {selectedProtocol?.symptom || 'Select a Protocol'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedProtocol ? (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Assessment</h4>
                      <ul className="text-sm list-disc list-inside text-gray-600">
                        {selectedProtocol.assessment?.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Pharmacologic Interventions</h4>
                      {selectedProtocol.pharmacologic?.map((item, idx) => (
                        <div key={idx} className="bg-gray-50 p-2 rounded mb-2">
                          <p className="font-medium text-sm">{item.intervention}</p>
                          <p className="text-xs text-gray-500">{item.indication}</p>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Non-Pharmacologic</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProtocol.non_pharmacologic?.map((item, idx) => (
                          <Badge key={idx} variant="outline">{item}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded">
                      <p className="text-sm"><strong>Escalation:</strong> {selectedProtocol.escalation}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Select a protocol to view details</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClinicalDecisionSupport;
